import { trace } from "@opentelemetry/api"
import { setPinoFiring } from "../telemetry/otel-logs"

/**
 * Returns { trace_id, span_id } from the active OTel span for log correlation.
 * Returns {} when no span is active or OTel has not been initialised yet.
 * Safe to call at any point in the app lifecycle — @opentelemetry/api is a no-op
 * until setupTelemetry() registers a provider.
 */
export const getOtelTraceContext = () => {
  try {
    const span = trace.getActiveSpan()
    if (!span) return {}
    const ctx = span.spanContext()
    if (!ctx.traceId || ctx.traceId === "00000000000000000000000000000000") return {}
    return { trace_id: ctx.traceId, span_id: ctx.spanId }
  } catch {
    return {}
  }
}

export const logLevels = Object.freeze({
  fatal: 50,
  error: 40,
  warn: 30,
  info: 20,
  debug: 10,
})

export const logLevelLabels = Object.freeze({
  fatal: "fatal",
  error: "error",
  warn: "warn",
  info: "info",
  debug: "debug",
})

export const shouldEmitLog = (level, minLevel = logLevels.info) => {
  if (level >= minLevel) {
    return true
  }
  return false
}

// Pretty print log objects from Pino
// eslint-disable-next-line no-console
export const prettyConsoleLogger = ({ logObject, minLevelLabel, consoleLogger = (o) => console.log(o) }) => {
  const { msg, module, level, label, time, ...otherProps } = logObject
  if (shouldEmitLog(level, logLevels[minLevelLabel])) {
    const labelUpper = label.toUpperCase()
    const dateStamp = time ? `[${new Date(time).toISOString()}]` : ""
    if (msg && module) {
      consoleLogger(`${dateStamp} [${labelUpper}] ${module} - ${msg}`)
    } else if (msg) {
      consoleLogger(`[${dateStamp} [${labelUpper}] ${msg}`)
    }
    if (otherProps && Object.keys(otherProps).length > 0) {
      consoleLogger(otherProps)
    }
  }
}

// Wrap a console call with the pino-firing flag so console-capture.js skips
// OTel re-emit for these — transmit.send handles the OTel path for Pino logs.
const withPinoFlag = (fn) => {
  setPinoFiring(true)
  try {
    fn()
  } finally {
    setPinoFiring(false)
  }
}

// Custom browser 'write()' function config for Pino logger instance using pretty print function
/* eslint-disable no-console */
export const consoleLogWriterConfig = (minLevelLabel) => ({
  fatal: (logObject) => withPinoFlag(() => prettyConsoleLogger({ logObject, minLevelLabel, consoleLogger: (o) => console.error(o) })),
  error: (logObject) => withPinoFlag(() => prettyConsoleLogger({ logObject, minLevelLabel, consoleLogger: (o) => console.error(o) })),
  warn: (logObject) => withPinoFlag(() => prettyConsoleLogger({ logObject, minLevelLabel, consoleLogger: (o) => console.warn(o) })),
  info: (logObject) => withPinoFlag(() => prettyConsoleLogger({ logObject, minLevelLabel, consoleLogger: (o) => console.info(o) })),
  debug: (logObject) => withPinoFlag(() => prettyConsoleLogger({ logObject, minLevelLabel, consoleLogger: (o) => console.debug(o) })),
})
/* eslint-enable no-console */
